import { useState } from 'react';
import { useChat, isScopedThreadId } from '../state/chat';
import { flushProfFigure, selectProfFigure } from '../state/prof-figure';
import { useTokens } from '../state/theme';
import { profFigureAssetRef } from '../types/prof-figure';

/** One figure for this conversation, never the last image elsewhere on screen. */
export function ProfFigureContext({ threadId }: { threadId: string }) {
  const tokens = useTokens();
  const sessionId = useChat((s) => isScopedThreadId(threadId) ? s.scopedSessionIds[threadId] : s.chiediSessionId);
  const snapshot = useChat((s) => sessionId ? s.profFiguresBySession[sessionId] : undefined);
  const status = useChat((s) => sessionId ? s.profFigureImageStatusBySession[sessionId] : undefined);
  const pending = useChat((s) => s.pendingProfFiguresByThread[threadId]);
  const notice = useChat((s) => s.profFigureNoticesByThread[threadId]);
  const figure = pending ? pending.preview : snapshot?.figure;
  const imageRef = profFigureAssetRef(figure?.imageRef);
  const [failedImage, setFailedImage] = useState<string | null>(null);
  if (!figure && !pending && !notice) return null;
  const saving = pending?.status === 'saving';
  const unavailable = status === 'unavailable' || (imageRef && failedImage === imageRef);

  return (
    <section aria-label="Figura nella conversazione" data-testid="prof-figure-context"
      style={{ margin: '6px 16px', padding: 10, border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.md, background: tokens.surface, flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {imageRef && failedImage !== imageRef && (
          <img src={`/${imageRef}`} alt={figure?.label || 'Figura selezionata'}
            onError={() => setFailedImage(imageRef)}
            style={{ width: 64, height: 64, objectFit: 'contain', flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0, fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.text }}>
          <strong>{figure ? 'Figura nella conversazione' : 'Rimozione della figura'}</strong>
          {figure?.label && <div style={{ marginTop: 3 }}>{figure.label}</div>}
          {pending && <div role="status" style={{ marginTop: 3, color: tokens.textMuted }}>
            {saving ? 'Salvo la modifica…' : pending.status === 'error' ? 'Modifica non ancora salvata' : 'Pronta per il prossimo messaggio'}
          </div>}
          {!pending && unavailable && <div role="status">La figura non è disponibile in questo momento.</div>}
        </div>
        {figure && <button type="button" disabled={saving}
          onClick={() => { void selectProfFigure(threadId, null); }}
          style={{ minHeight: 44, border: 0, background: 'transparent', color: tokens.accentInk,
            cursor: saving ? 'wait' : 'pointer', fontSize: 12, maxWidth: 90 }}>
          Rimuovi figura
        </button>}
      </div>
      {notice && <div role="alert" style={{ marginTop: 6, fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.text }}>
        {notice}
      </div>}
      {pending?.status === 'error' && <button type="button" onClick={() => { void flushProfFigure(threadId); }}
        style={{ marginTop: 6, minHeight: 44, color: tokens.accentInk, background: tokens.accentSoft,
          border: 0, borderRadius: tokens.radius.sm, padding: '6px 12px' }}>
        Riprova a salvare la figura
      </button>}
    </section>
  );
}
